import { useEffect, useMemo, useState } from 'react';
import { App, DatePicker, Form, Popconfirm, Select, Space, Tooltip, Typography } from 'antd';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import KPICard from '@/components/ui/analytics/KPICard';
import Table from '@/components/ui/table/Table';
import { useTenantDateFormat } from '@hooks/useTenantDateFormat';
import { DashboardContainer, KPICardsGrid, StudentsHeaderLeft, StudentsHeaderRow, StudentsHeaderSub, StudentsHeaderTitle } from '../style';
import { formatMoney, getEntryDisplayDayjs, parseMoneyNumber, toDayjs } from './shared';
import { AppModal } from '@/components/ui/AppModal';
import { AppInput } from '@/components/ui/AppInput';
import { Button } from '@components/ui/Button';
import { useAuthStore } from '@stores/auth.store';
import { FINANCE_ENTRY_TYPES, subscribeFinanceEntries } from '@services/registry';
import { useFinanceAccounts } from '@/hooks/tenant/finance/useFinanceAccounts';
import { useFinanceSummary } from '@/hooks/tenant/finance/useFinanceSummary';
import { usePostFinanceEntry } from '@/hooks/tenant/finance/usePostFinanceEntry';
import { useUpdateFinanceEntry } from '@/hooks/tenant/finance/useUpdateFinanceEntry';
import { useDeleteFinanceEntry } from '@/hooks/tenant/finance/useDeleteFinanceEntry';
import { useAbility } from '@/ability/AbilityProvider';
import dayjs from 'dayjs';

const { Text } = Typography;

export default function ExpenseLedger() {
    const { message } = App.useApp();
    const ability = useAbility();
    const canManage = !!ability?.can?.('manage', 'ExpenseLedger');
    const { dateFormat } = useTenantDateFormat();
    const [searchText, setSearchText] = useState('');
    const [createOpen, setCreateOpen] = useState(false);
    const [form] = Form.useForm();
    const tenantId = useAuthStore((s) => s.user?.tenantId);
    const { rows: accounts } = useFinanceAccounts({ tenantId });
    const { summary } = useFinanceSummary({ tenantId });
    const postMutation = usePostFinanceEntry({ tenantId });
    const updateMutation = useUpdateFinanceEntry({ tenantId });
    const deleteMutation = useDeleteFinanceEntry({ tenantId });
    const [entries, setEntries] = useState([]);
    const [accountFilter, setAccountFilter] = useState(undefined);
    const [editingEntry, setEditingEntry] = useState(null); // row object

    useEffect(() => {
        if (!createOpen) return;
        // Modal mounts Form after open; set defaults after mount.
        setTimeout(() => {
            form.setFieldsValue({
                date: dayjs(),
                accountId: form.getFieldValue('accountId') || accounts?.[0]?.id,
            });
        }, 0);
    }, [createOpen, form, accounts]);

    useEffect(() => {
        if (!tenantId) {
            setEntries([]);
            return;
        }
        const unsub = subscribeFinanceEntries({
            tenantId,
            type: FINANCE_ENTRY_TYPES.EXPENSE,
            accountId: accountFilter,
            onNext: (next) => setEntries(next),
            onError: (err) => console.error(err),
        });
        return () => unsub?.();
    }, [tenantId, accountFilter]);

    const filteredRows = useMemo(() => {
        const q = (searchText || '').trim().toLowerCase();
        if (!q) return entries;
        return entries.filter((r) => {
            const particulars = (r.particulars || '').toLowerCase();
            const accountName =
                (accounts || []).find((a) => a.id === r.accountId)?.name || '';
            return particulars.includes(q) || accountName.toLowerCase().includes(q);
        });
    }, [entries, searchText, accounts]);

    const rowsWithAccount = useMemo(() => {
        const nameById = new Map((accounts || []).map((a) => [a.id, a.name || a.id]));
        return (filteredRows || []).map((r) => ({
            ...r,
            accountName: nameById.get(r.accountId) || '—',
        }));
    }, [filteredRows, accounts]);

    const totals = useMemo(() => {
        if (!tenantId) return { total: 0 };
        if (accountFilter) {
            const acc = (accounts || []).find((a) => a.id === accountFilter) || null;
            return { total: Number(acc?.totalExpense || 0) || 0 };
        }
        return { total: Number(summary?.totalExpense || 0) || 0 };
    }, [tenantId, accountFilter, summary, accounts]);

    const columns = useMemo(() => ([
        {
            title: 'Date',
            key: 'date',
            render: (_, row) => {
                const d = getEntryDisplayDayjs(row);
                return d ? d.format(dateFormat) : '—';
            },
        },
        {
            title: 'Account',
            dataIndex: 'accountName',
            key: 'account',
            render: (v) => v || '—',
        },
        {
            title: 'Particulars',
            dataIndex: 'particulars',
            key: 'particulars',
            render: (v) => v || '—',
        },
        {
            title: 'Amount',
            key: 'amount',
            render: (_, row) => (
                <Text style={{ color: (Number(row.amount) || 0) > 0 ? '#ef4444' : undefined }}>
                    {row.amount ? formatMoney(row.amount) : '—'}
                </Text>
            ),
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, row) => {
                if (!canManage) return <Text type="secondary">—</Text>;
                const busy = deleteMutation.isPending || updateMutation.isPending;
                return (
                    <Space size={10}>
                        <button
                            type="button"
                            disabled={busy}
                            onClick={(e) => {
                                e.stopPropagation();
                                setEditingEntry(row);
                                setCreateOpen(true);
                                setTimeout(() => {
                                    form.setFieldsValue({
                                        accountId: row.accountId,
                                        date: getEntryDisplayDayjs(row) || dayjs(),
                                        particulars: row.particulars || '',
                                        amount: formatMoney(row.amount || 0),
                                    });
                                }, 0);
                            }}
                            style={{ border: 'none', background: 'transparent', cursor: busy ? 'not-allowed' : 'pointer', padding: 0 }}
                            aria-label="Edit"
                        >
                            <EditOutlined />
                        </button>
                        <Popconfirm
                            title="Delete expense?"
                            description="This will adjust analytics and account totals."
                            okText="Delete"
                            okButtonProps={{ danger: true }}
                            onConfirm={() => void deleteMutation.mutateAsync({ entryId: row.id }).then(() => message.success('Expense deleted')).catch((e) => message.error(e?.message || 'Failed to delete'))}
                        >
                            <button
                                type="button"
                                disabled={busy}
                                onClick={(e) => e.stopPropagation()}
                                style={{ border: 'none', background: 'transparent', cursor: busy ? 'not-allowed' : 'pointer', padding: 0, color: '#ef4444' }}
                                aria-label="Delete"
                            >
                                <DeleteOutlined />
                            </button>
                        </Popconfirm>
                    </Space>
                );
            },
        },
    ]), [dateFormat, canManage]);

    const openCreate = () => {
        form.resetFields();
        setEditingEntry(null);
        setCreateOpen(true);
    };

    const onCreate = async () => {
        if (!canManage) {
            message.error('You have view-only access for Expense Ledger.');
            return;
        }
        const v = await form.validateFields();
        const amt = parseMoneyNumber(v.amount);
        if (!amt || amt <= 0) {
            form.setFields([{ name: 'amount', errors: ['Amount must be greater than 0'] }]);
            return;
        }
        try {
            if (editingEntry?.id) {
                await updateMutation.mutateAsync({
                    entryId: editingEntry.id,
                    patch: {
                        accountId: v.accountId,
                        date: v.date?.toDate?.() || v.date || new Date(),
                        particulars: (v.particulars || '').trim(),
                        amount: amt,
                    },
                });
                message.success('Expense updated');
            } else {
                await postMutation.mutateAsync({
                    accountId: v.accountId,
                    type: FINANCE_ENTRY_TYPES.EXPENSE,
                    date: v.date?.toDate?.() || v.date || new Date(),
                    particulars: (v.particulars || '').trim(),
                    amount: amt,
                });
                message.success('Expense saved');
            }
            setCreateOpen(false);
            setEditingEntry(null);
        } catch (e) {
            message.error(e?.message || (editingEntry?.id ? 'Failed to update expense' : 'Failed to save expense'));
        }
    };

    return (
        <DashboardContainer>
            <StudentsHeaderRow>
                <StudentsHeaderLeft>
                    <StudentsHeaderTitle>Expense Ledger</StudentsHeaderTitle>
                    <StudentsHeaderSub>All expense entries in one place.</StudentsHeaderSub>
                </StudentsHeaderLeft>
                <Space size={10} wrap>
                    <Select
                        value={accountFilter}
                        onChange={(v) => setAccountFilter(v || undefined)}
                        placeholder="All accounts"
                        allowClear
                        style={{ minWidth: 220 }}
                        options={(accounts || []).map((a) => ({
                            value: a.id,
                            label: `${a.name}${a.type ? ` (${a.type})` : ''}`,
                        }))}
                    />
                    <Tooltip title={!accounts?.length ? 'Create an account first (Finance → Account Management).' : !canManage ? 'View-only access' : undefined}>
                        <span>
                            <Button type="primary" onClick={openCreate} disabled={!accounts?.length || !canManage}>
                                Add Expense
                            </Button>
                        </span>
                    </Tooltip>
                </Space>
            </StudentsHeaderRow>

            <KPICardsGrid $columns={1} $rows={1} $height="auto" style={{ marginBottom: 16 }}>
                <KPICard title="Total Expense" value={formatMoney(totals.total)} />
            </KPICardsGrid>

            <Table
                title="Ledger"
                columns={columns}
                dataSource={rowsWithAccount}
                loading={postMutation.isPending}
                emptyText="No expenses yet"
                searchValue={searchText}
                onSearchChange={setSearchText}
                pagination={true}
                pageSize={10}
                showSearch={true}
                showPagination={true}
            />

            <AppModal
                title={editingEntry?.id ? 'Edit expense' : 'Add expense'}
                open={createOpen}
                onCancel={() => {
                    setCreateOpen(false);
                    setEditingEntry(null);
                }}
                onOk={() => void onCreate()}
                okText={editingEntry?.id ? 'Save changes' : 'Save expense'}
                size="md"
                destroyOnClose
                okButtonProps={{ disabled: postMutation.isPending || updateMutation.isPending || !canManage }}
                confirmLoading={postMutation.isPending || updateMutation.isPending}
            >
                <Form
                    form={form}
                    layout="vertical"
                    preserve={false}
                    initialValues={{ amount: '', date: dayjs() }}
                >
                    <Form.Item
                        name="accountId"
                        label="Account"
                        rules={[{ required: true, message: 'Select an account' }]}
                    >
                        <Select
                            placeholder="Select account"
                            options={(accounts || []).map((a) => ({
                                value: a.id,
                                label: `${a.name}${a.type ? ` (${a.type})` : ''}`,
                            }))}
                        />
                    </Form.Item>
                    <Form.Item
                        name="date"
                        label="Date"
                        rules={[{ required: true, message: 'Select a date' }]}
                    >
                        <DatePicker style={{ width: '100%' }} format={dateFormat} />
                    </Form.Item>
                    <Form.Item
                        name="particulars"
                        label="Particulars"
                        rules={[{ required: true, message: 'Enter particulars' }]}
                    >
                        <AppInput placeholder="e.g. Stationery" />
                    </Form.Item>
                    <Form.Item
                        name="amount"
                        label="Amount"
                        rules={[{ required: true, message: 'Enter amount' }]}
                    >
                        <AppInput isPrice placeholder="0" />
                    </Form.Item>
                </Form>
            </AppModal>
        </DashboardContainer>
    );
}

